import type { FilterOption, FilterOptionGroup } from '@wundo/ui';
import type { SendableNotificationChannel } from '@wundo/shared/notificationChannel.ts';
import {
  notificationHistoryGroups,
  type NotificationHistoryGroup,
  type NotificationHistoryKind,
  type NotificationSendOrigin,
} from '@wundo/shared/notificationHistory.ts';
import type { NotificationSendFilters } from '@/lib/notificationHistory.ts';
import { HISTORY_CHANNEL_LABELS } from './historyLabels.ts';

/** Everything, everything but one group, one group, or one kind. */
export type TypeFilter =
  | 'all'
  | `except:${NotificationHistoryGroup}`
  | `group:${NotificationHistoryGroup}`
  | `kind:${string}`;

export interface HistoryFilters {
  type: TypeFilter;
  channel: SendableNotificationChannel | '';
  sentBy: NotificationSendOrigin | '';
}

/* The meetings outnumber everything else together, so the list starts without them. */
export const DEFAULT_HISTORY_FILTERS: HistoryFilters = {
  type: 'except:meeting',
  channel: '',
  sentBy: '',
};

export const NO_HISTORY_FILTERS: HistoryFilters = {
  type: 'all',
  channel: '',
  sentBy: '',
};

const GROUPS: Record<NotificationHistoryGroup, { label: string; all: string }> = {
  meeting: { label: 'Meetings', all: 'All meetings' },
  moment: { label: 'Wundo’s moments', all: 'All of Wundo’s moments' },
  journey: { label: 'Journeys', all: 'All journeys' },
  email: { label: 'Emails', all: 'All emails' },
};

/* Each group can be picked whole, before its kinds arrive, and then kind by kind. */
export function typeOptions(
  kinds: NotificationHistoryKind[],
): (FilterOption<TypeFilter> | FilterOptionGroup<TypeFilter>)[] {
  return [
    { value: 'except:meeting', label: 'All except meetings' },
    { value: 'all', label: 'All notifications' },
    ...notificationHistoryGroups.map((group) => ({
      label: GROUPS[group].label,
      options: [
        { value: `group:${group}` as const, label: GROUPS[group].all },
        ...kinds
          .filter((entry) => entry.group === group)
          .map((entry) => ({ value: `kind:${entry.kind}` as const, label: entry.label })),
      ],
    })),
  ];
}

export const CHANNEL_OPTIONS: FilterOption<HistoryFilters['channel']>[] = [
  { value: '', label: 'All channels' },
  { value: 'msteams', label: HISTORY_CHANNEL_LABELS.msteams },
  { value: 'slack', label: HISTORY_CHANNEL_LABELS.slack },
  { value: 'email', label: HISTORY_CHANNEL_LABELS.email },
];

export const SENT_BY_OPTIONS: FilterOption<HistoryFilters['sentBy']>[] = [
  { value: '', label: 'All senders' },
  { value: 'automatic', label: 'Automatic' },
  { value: 'console', label: 'From the console' },
];

function typeParams(type: TypeFilter): NotificationSendFilters {
  if (type === 'all') return {};
  const [scope, name] = type.split(':');
  if (scope === 'except') return { exclude_group: name as NotificationHistoryGroup };
  if (scope === 'group') return { group: name as NotificationHistoryGroup };
  return { kind: name };
}

export function toNotificationSendFilters(
  filters: HistoryFilters,
): NotificationSendFilters {
  return {
    ...typeParams(filters.type),
    ...(filters.channel && { channel: filters.channel }),
    ...(filters.sentBy && { sent_by: filters.sentBy }),
  };
}
